import React, {memo} from 'react';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import {BottomSheetHandleProps} from '@gorhom/bottom-sheet';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';

const AnimatedContainer = styled(Animated.View)`
  align-items: center;
  justify-content: center;
  height: ${({theme}) => theme.text.s2}px;
  background-color: ${({theme}) => theme.screenBackground};
  /* iOS Shadow */
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0px -10px;
  shadow-opacity: 0.1;
  shadow-radius: 5px;
`;
const ContentContainer = styled.TouchableOpacity`
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const AnimatedIndicator = styled(Animated.View)`
  position: absolute;
  width: ${({theme}) => theme.text.s7}px;
  height: 3px;
  background-color: ${({theme}) => theme.settings.icon};
  border-radius: ${({theme}) => theme.text.s7}px;
`;

interface Props extends BottomSheetHandleProps {
  closeBottomSheet?: () => void;
}

const BottomSheetHandle: React.FC<Props> = ({
  animatedIndex,
  closeBottomSheet,
}) => {
  const theme = useTheme() as Theme;
  // Animations
  const indicatorTransformOriginY = useDerivedValue(() =>
    interpolate(animatedIndex.value, [0, -1], [0, -1], Extrapolation.CLAMP),
  );

  const containerAnimatedStyle = useAnimatedStyle(() => {
    const borderTopRadius = interpolate(
      animatedIndex.value,
      [0, -1],
      [20, 0],
      Extrapolation.CLAMP,
    );

    return {
      borderTopLeftRadius: borderTopRadius,
      borderTopRightRadius: borderTopRadius,
    };
  });

  const leftIndicatorAnimatedStyle = useAnimatedStyle(() => {
    const leftIndicatorRotate = interpolate(
      animatedIndex.value,
      [0, -0.3],
      [0.5, -0.5],
      Extrapolation.CLAMP,
    );
    return {
      transform: [
        {rotate: `${leftIndicatorRotate}rad`},
        {translateX: -theme.text.s7 / 2 + 1},
        {translateY: indicatorTransformOriginY.value},
      ],
    };
  });

  const rightIndicatorAnimatedStyle = useAnimatedStyle(() => {
    const rightIndicatorRotate = interpolate(
      animatedIndex.value,
      [0, -0.3],
      [-0.5, 0.5],
      Extrapolation.CLAMP,
    );
    return {
      transform: [
        {rotate: `${rightIndicatorRotate}rad`},
        {translateX: theme.text.s7 / 2 - 1},
        {translateY: indicatorTransformOriginY.value},
      ],
    };
  });

  return (
    <AnimatedContainer style={containerAnimatedStyle}>
      <ContentContainer onPress={closeBottomSheet}>
        <AnimatedIndicator style={leftIndicatorAnimatedStyle} />
        <AnimatedIndicator style={rightIndicatorAnimatedStyle} />
      </ContentContainer>
    </AnimatedContainer>
  );
};

export default memo(BottomSheetHandle);
